import React from 'react';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { SFGrey } from '../../SFColors/SFColors';
import { hexToRgba } from '../../Helpers';

const SCROLL_BOX_MIN_HEIGHT = 20;
const SCROLL_BOX_MIN_WIDTH = 20;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      height: '100%',
      position: 'relative',
      boxSizing: 'border-box'
    },
    withHorizontalScroll: {
      paddingBottom: 12
    },
    container: {
      height: '100%',
      overflow: 'auto',
      scrollbarWidth: 'none',
      msOverflowStyle: 'none',

      '&::-webkit-scrollbar': {
        display: 'none'
      }
    },
    vScrollBar: {
      height: '100%',
      width: (props: { size: SFScrollableSize }): string =>
        props.size === 'default' ? '9px' : '15px',
      position: 'absolute',
      right: 3,
      top: 0,
      bottom: 0,
      '@media print': {
        display: 'none'
      }
    },
    vScrollThumb: {
      marginLeft: '3px',
      width: (props: { size: SFScrollableSize }): string =>
        props.size === 'default' ? '6px' : '9px',
      position: 'absolute',
      borderRadius: '3px',
      backgroundColor:
        theme.palette.type === 'light'
          ? hexToRgba(SFGrey.A100 as string, 0.3)
          : hexToRgba(SFGrey[500], 0.3)
    },
    hScrollBar: {
      position: 'absolute',
      width: '100%',
      height: (props: { size: SFScrollableSize }): string =>
        props.size === 'default' ? '9px' : '15px',
      left: 0,
      right: 0,
      bottom: 0,
      '@media print': {
        display: 'none'
      }
    },
    hScrollThumb: {
      height: (props: { size: SFScrollableSize }): string =>
        props.size === 'default' ? '6px' : '9px',
      position: 'absolute',
      borderRadius: '3px',
      backgroundColor:
        theme.palette.type === 'light'
          ? hexToRgba(SFGrey.A100 as string, 0.3)
          : hexToRgba(SFGrey[500], 0.3)
    }
  })
);

const hasScrollVertical = (elem: HTMLDivElement): boolean =>
  elem.scrollHeight > elem.clientHeight;

const hasScrollHorizontal = (elem: HTMLDivElement): boolean =>
  elem.scrollWidth > elem.clientWidth;

export interface SFScrollData {
  host: HTMLDivElement;
  verticalScroll: number;
  horizontalScroll: number;
}

export interface SFScrollableRefHandler {
  scrollToTop: (behavior?: ScrollBehavior) => void;
}

export type SFScrollableSize = 'default' | 'large';

export interface SFScrollableContextProps {
  isAlwaysVisible: boolean;
  size: SFScrollableSize;
}
export const SFScrollableContext = React.createContext({
  isAlwaysVisible: false,
  size: 'default'
});
export interface SFScrollableProps {
  className?: string;
  containerClassName?: string;
  children: React.ReactNode;
  isAlwaysVisible?: boolean;
  size?: SFScrollableSize;
  onScroll?: (data: SFScrollData) => void;
  onScrollBottom?: () => void;
}

export const SFScrollable = React.forwardRef(
  (
    {
      className,
      containerClassName,
      children,
      onScroll,
      onScrollBottom,
      ...props
    }: SFScrollableProps,
    ref: React.Ref<SFScrollableRefHandler>
  ) => {
    const {
      isAlwaysVisible: contextIsAlwaysVisible,
      size: contextSize
    } = React.useContext(SFScrollableContext);

    const isAlwaysVisible =
      props.isAlwaysVisible ?? contextIsAlwaysVisible ?? false;
    const size = props.size ?? contextSize ?? 'default';
    const classes = useStyles({ size: size as SFScrollableSize });

    const scrollHostRef: React.RefObject<HTMLDivElement> = React.useRef<HTMLDivElement>(
      null
    );

    React.useImperativeHandle(ref, () => ({
      scrollToTop(behavior?: ScrollBehavior): void {
        if (scrollHostRef.current) {
          scrollHostRef.current.scrollTo({ top: 0, behavior });
        }
      }
    }));

    const [
      hasHorizontalScroll,
      setHasHorizontalScroll
    ] = React.useState<boolean>(false);

    const [
      verticalScrollHeight,
      setVerticalScrollHeight
    ] = React.useState<number>(SCROLL_BOX_MIN_HEIGHT);

    const [
      horizontalScrollWidth,
      setHorizontalScrollWidth
    ] = React.useState<number>(SCROLL_BOX_MIN_WIDTH);

    const [verticalScrollTop, setVerticalScrollTop] = React.useState<number>(0);

    const [
      horizontalScrollLeft,
      setHorizontalScrollLeft
    ] = React.useState<number>(0);

    const [showVerticalScroll, setShowVerticalScroll] = React.useState<boolean>(
      false
    );

    const [
      showHorizontalScroll,
      setShowHorizontalScroll
    ] = React.useState<boolean>(false);

    const [isVerticalDragging, setIsVerticalDragging] = React.useState<boolean>(
      false
    );

    const [
      isHorizontalDragging,
      setIsHorizontalDragging
    ] = React.useState<boolean>(false);

    const [lastVerticalPos, setLastVerticalPos] = React.useState<number>(0);
    const [lastHorizontalPos, setLastHorizontalPos] = React.useState<number>(0);

    const updateScrollbar = (elem: HTMLDivElement): void => {
      const { clientHeight, clientWidth, scrollHeight, scrollWidth } = elem;

      if (hasScrollHorizontal(elem)) {
        const scrollThumbWidth: number = Math.max(
          (clientWidth / scrollWidth) * clientWidth,
          SCROLL_BOX_MIN_WIDTH
        );

        if (scrollThumbWidth !== horizontalScrollWidth) {
          setHorizontalScrollWidth(scrollThumbWidth);
        }

        if (!hasHorizontalScroll) {
          setHasHorizontalScroll(true);
        }
      } else if (hasHorizontalScroll) {
        setHasHorizontalScroll(false);
      }

      if (hasScrollVertical(elem)) {
        const scrollThumbHeight: number = Math.max(
          (clientHeight / scrollHeight) * clientHeight,
          SCROLL_BOX_MIN_HEIGHT
        );

        if (verticalScrollHeight !== scrollThumbHeight) {
          setVerticalScrollHeight(scrollThumbHeight);
        }
      }
    };

    React.useEffect(() => {
      const elem: HTMLDivElement = scrollHostRef.current as HTMLDivElement;

      const resizeObserver = new ResizeObserver(() => {
        if (isAlwaysVisible) {
          setShowVerticalScroll(hasScrollVertical(elem));
          setShowHorizontalScroll(hasScrollHorizontal(elem));
        }
        updateScrollbar(elem);
      });
      resizeObserver.observe(elem);

      // cleanup
      return (): void => {
        resizeObserver.unobserve(elem);
      };
    }, []);

    React.useEffect(() => {
      const elem: HTMLDivElement = scrollHostRef.current as HTMLDivElement;
      if (elem && isAlwaysVisible) {
        setShowVerticalScroll(hasScrollVertical(elem));
        setShowHorizontalScroll(hasScrollHorizontal(elem));
      }
    }, [isAlwaysVisible]);

    React.useEffect(() => {
      if (scrollHostRef.current) {
        updateScrollbar(scrollHostRef.current as HTMLDivElement);

        if (isAlwaysVisible) {
          setShowVerticalScroll(hasScrollVertical(scrollHostRef.current));
          setShowHorizontalScroll(hasScrollHorizontal(scrollHostRef.current));
        }
      }
    }, [children]);

    const onDocumentMouseMove = React.useCallback(
      (e: MouseEvent) => {
        if (scrollHostRef.current) {
          if (isVerticalDragging) {
            e.preventDefault();
            e.stopPropagation();

            const {
              scrollHeight,
              offsetHeight,
              offsetTop
            } = scrollHostRef.current;
            const deltaY = e.clientY - lastVerticalPos;
            const percentage = deltaY * (scrollHeight / offsetHeight);

            setLastVerticalPos(e.clientY);

            setVerticalScrollTop(
              Math.min(
                Math.max(offsetTop, verticalScrollTop + deltaY),
                offsetHeight - verticalScrollHeight + offsetTop
              )
            );

            scrollHostRef.current.scrollTop = Math.min(
              scrollHostRef.current.scrollTop + percentage,
              scrollHeight - offsetHeight
            );
          } else if (isHorizontalDragging) {
            e.preventDefault();
            e.stopPropagation();

            const { scrollWidth, offsetWidth } = scrollHostRef.current;
            const deltaX = e.clientX - lastHorizontalPos;
            const percentage = deltaX * (scrollWidth / offsetWidth);

            setLastHorizontalPos(e.clientX);
            setHorizontalScrollLeft(
              Math.min(
                Math.max(0, horizontalScrollLeft + deltaX),
                offsetWidth - horizontalScrollWidth
              )
            );

            scrollHostRef.current.scrollLeft = Math.min(
              scrollHostRef.current.scrollLeft + percentage,
              scrollWidth - offsetWidth
            );

            if (onScroll) {
              onScroll({
                verticalScroll: verticalScrollTop,
                horizontalScroll: horizontalScrollLeft,
                host: scrollHostRef.current
              });
            }
          }
        }
      },
      [[isHorizontalDragging, isVerticalDragging]]
    );

    const onDocumentMouseUp = React.useCallback(
      (e: MouseEvent) => {
        if (isVerticalDragging) {
          e.preventDefault();
          setIsVerticalDragging(false);

          if (!isAlwaysVisible) {
            setShowVerticalScroll(false);
          }
        } else if (isHorizontalDragging) {
          e.preventDefault();
          setIsHorizontalDragging(false);

          if (!isAlwaysVisible) {
            setShowHorizontalScroll(false);
          }
        }
      },
      [[isHorizontalDragging, isVerticalDragging]]
    );

    React.useEffect(() => {
      // handle scrollbar dragging
      document.addEventListener('mousemove', onDocumentMouseMove);
      document.addEventListener('mouseup', onDocumentMouseUp);
      document.addEventListener('mouseleave', onDocumentMouseUp);

      // cleanup
      return (): void => {
        document.removeEventListener('mousemove', onDocumentMouseMove);
        document.removeEventListener('mouseup', onDocumentMouseUp);
        document.removeEventListener('mouseleave', onDocumentMouseUp);
      };
    }, [onDocumentMouseMove, onDocumentMouseUp]);

    const onMouseOver = (): void => {
      if (scrollHostRef.current) {
        if (hasScrollVertical(scrollHostRef.current) && !showVerticalScroll) {
          setShowVerticalScroll(true);
        }

        if (
          hasScrollHorizontal(scrollHostRef.current) &&
          !showHorizontalScroll
        ) {
          setShowHorizontalScroll(true);
        }
      }
    };

    const onMouseOut = (): void => {
      if (!isAlwaysVisible && showHorizontalScroll && !isHorizontalDragging) {
        setShowHorizontalScroll(false);
      }

      if (!isAlwaysVisible && showVerticalScroll && !isVerticalDragging) {
        setShowVerticalScroll(false);
      }
    };

    const onVerticalScrollMouseDown: React.MouseEventHandler<HTMLDivElement> = (
      e
    ) => {
      e.preventDefault();
      e.stopPropagation();

      setLastVerticalPos(e.clientY);
      setIsVerticalDragging(true);
    };

    const onHorizontalScrollMouseDown: React.MouseEventHandler<HTMLDivElement> = (
      e
    ) => {
      e.preventDefault();
      e.stopPropagation();

      setLastHorizontalPos(e.clientX);
      setIsHorizontalDragging(true);
    };

    const onHostScroll = (): void => {
      if (scrollHostRef.current) {
        const {
          scrollTop,
          scrollHeight,
          scrollLeft,
          scrollWidth,
          offsetHeight,
          offsetWidth,
          clientHeight
        } = scrollHostRef.current;

        let newTop = (scrollTop / scrollHeight) * offsetHeight;
        newTop = Math.min(newTop, offsetHeight - verticalScrollHeight);

        let newLeft = (scrollLeft / scrollWidth) * offsetWidth;
        newLeft = Math.min(newLeft, offsetWidth - horizontalScrollLeft);

        if (newTop !== verticalScrollTop) {
          setVerticalScrollTop(newTop);
        }

        if (newLeft !== horizontalScrollLeft) {
          setHorizontalScrollLeft(newLeft);
        }

        if (scrollHeight - scrollTop - clientHeight < 0.5 && onScrollBottom) {
          onScrollBottom();
        }

        if (onScroll) {
          onScroll({
            verticalScroll: newTop,
            horizontalScroll: newLeft,
            host: scrollHostRef.current
          });
        }
      }
    };

    return (
      <div
        className={`${classes.root} ${
          hasHorizontalScroll ? classes.withHorizontalScroll : ''
        } ${className || ''}`}
        onMouseOver={onMouseOver}
        onTouchStart={onMouseOver}
        onMouseOut={onMouseOut}
        onTouchEnd={onMouseOut}
      >
        <div
          className={`${classes.container} ${containerClassName || ''}`}
          ref={scrollHostRef}
          onScroll={onHostScroll}
          tabIndex={0}
        >
          {children}
        </div>

        <div
          className={classes.vScrollBar}
          style={{ opacity: showVerticalScroll ? 1 : 0 }}
        >
          <div
            className={classes.vScrollThumb}
            style={{ height: verticalScrollHeight, top: verticalScrollTop }}
            onMouseDown={onVerticalScrollMouseDown}
          />
        </div>

        <div
          className={classes.hScrollBar}
          style={{ opacity: showHorizontalScroll ? 1 : 0 }}
        >
          <div
            className={classes.hScrollThumb}
            style={{ width: horizontalScrollWidth, left: horizontalScrollLeft }}
            onMouseDown={onHorizontalScrollMouseDown}
          />
        </div>
      </div>
    );
  }
);
